import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { EnvelopeSimple, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

function savedChallenge() {
  try { return JSON.parse(sessionStorage.getItem("portal-verification")); }
  catch { return null; }
}

export default function VerifyEmail() {
  const { user, verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(savedChallenge);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [now, setNow] = useState(Date.now());
  const token = challenge?.verification_token;
  const update = (data) => {
    setChallenge((previous) => {
      const next = { ...previous, ...data };
      sessionStorage.setItem("portal-verification", JSON.stringify(next));
      return next;
    });
  };
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let active = true;
    api.post("/auth/verification-status", { verification_token: token })
      .then(({ data }) => { if (active) update(data); })
      .catch((err) => { if (active) { setError(formatApiError(err.response?.data?.detail)); setUnavailable(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);
  if (user) return <Navigate to={user.role === "member" ? "/profile" : "/dashboard"} replace />;
  const expires = Math.max(0, Math.ceil((Date.parse(challenge?.code_expires_at) - now) / 1000)) || 0;
  const resendWait = Math.max(0, Math.ceil((Date.parse(challenge?.resend_after) - now) / 1000)) || 0;
  const locked = challenge?.attempts_remaining === 0;
  const startAgain = () => { sessionStorage.removeItem("portal-verification"); navigate(challenge?.member_type ? `/join/${challenge.member_type}` : "/join"); };
  const verify = async (event) => {
    event.preventDefault(); setError(""); setBusy(true);
    try {
      await verifyEmail(token, code);
      sessionStorage.removeItem("portal-verification");
      toast.success("Email verified. Complete your membership application.");
      navigate("/profile", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      try { const { data } = await api.post("/auth/verification-status", { verification_token: token }); update(data); }
      catch { setUnavailable(true); }
    } finally { setBusy(false); }
  };
  const resend = async () => {
    setResending(true); setError("");
    try {
      const { data } = await api.post("/auth/resend-verification", { verification_token: token });
      update(data); setCode(""); setNow(Date.now()); toast.success("A new verification code has been sent. Use the latest code.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setResending(false); }
  };
  return (
    <div className="min-h-screen bg-background px-6 py-10 flex items-center justify-center">
      <div className="absolute top-6 right-6"><ThemeToggle /></div>
      <main className="w-full max-w-md animate-fade-up">
        <Link to="/login" data-testid="verification-back-login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8"><ArrowLeft />Back to sign in</Link>
        <div className="rounded-md border border-border bg-card p-6 sm:p-8">
          <div className="inline-flex p-3 rounded-md bg-primary/10 text-primary mb-5"><EnvelopeSimple size={28} weight="bold" /></div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email verification</p>
          <h1 data-testid="verify-email-heading" className="font-display text-3xl font-bold tracking-tight mt-2">Check your inbox</h1>
          {!token ? <p className="mt-4 text-sm text-muted-foreground" data-testid="verification-no-challenge">Start an application to receive your verification code.</p> : <>
            <p className="mt-4 text-sm text-muted-foreground" data-testid="verification-destination">We sent a 6-digit code to <strong className="text-foreground">{challenge.email}</strong>. Your account will only be activated after verification.</p>
            {!unavailable && <form onSubmit={verify} className="mt-6 space-y-4" data-testid="email-verification-form">
              <div className="space-y-2"><Label htmlFor="verification-code">6-digit verification code</Label>
                <Input id="verification-code" data-testid="verification-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" required autoFocus value={code} disabled={loading || busy || resending || locked || !expires} onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }} className="h-14 text-center font-mono text-2xl tracking-[0.4em]" />
              </div>
              <p className="text-xs text-muted-foreground" data-testid="verification-expiry">{loading ? "Checking verification…" : locked ? "Attempt limit reached. Please start again." : expires ? `Code expires in ${Math.floor(expires / 60)}:${String(expires % 60).padStart(2, "0")}` : "This code has expired. Request a new one below."}</p>
              <Button type="submit" className="w-full gap-2 h-11" disabled={loading || busy || resending || code.length !== 6 || !expires || locked} data-testid="verification-submit"><ShieldCheck size={18} />{busy ? "Verifying…" : "Verify email & continue"}</Button>
              <Button type="button" variant="outline" className="w-full" onClick={resend} disabled={loading || busy || resending || resendWait > 0 || locked} data-testid="verification-resend">{resending ? "Sending…" : resendWait > 0 ? `Resend code in ${resendWait}s` : "Resend verification code"}</Button>
            </form>}
          </>}
          {error && <p role="alert" data-testid="verification-error" className="text-sm text-destructive mt-4">{error}</p>}
          <p className="text-xs text-muted-foreground mt-5">Check your spam folder too. Use only the latest code and never share it.</p>
          <Button variant="link" onClick={startAgain} data-testid="verification-start-again" className="px-0 mt-3">{token && !unavailable && !locked ? "Change email or start again" : "Start a new application"}</Button>
        </div>
      </main>
    </div>
  );
}
